<modal
  [id]="id"
  [header]="header"
  [closeButtonEnabled]="!isImporting()"
  [enableBackdropClose]="false"
  [escapeToClose]="!isImporting()"
  [showHeaderDivider]="false"
  [showFooterDivider]="true"
  [widthClass]="'tw-w-[800px] tw-max-w-[95vw]'"
  [heightClass]="'tw-h-[480px] lg:tw-h-auto tw-max-h-[90vh]'"
  (onCloseModal)="onCloseModal()"
>
  <div slot="body" class="tw-flex tw-flex-col tw-gap-0 tw-font-inter">
    <!-- Steps indicator — full modal width, top + bottom border -->
    <div class="-tw-mx-6 -tw-mt-4 tw-mb-6 tw-flex tw-items-center tw-justify-between tw-border-y tw-border-solid tw-border-gray-200 tw-bg-gray-50 tw-px-6 tw-py-2">
      @for (step of wizardSteps; track step.number; let last = $last) {
        <div
          class="tw-flex tw-items-center tw-transition-opacity"
          [ngClass]="currentStep() >= step.number ? 'tw-opacity-100' : 'tw-opacity-60'"
        >
          <div
            class="tw-flex tw-h-6 tw-w-6 tw-items-center tw-justify-center tw-rounded-full tw-text-xs tw-font-bold tw-mr-2"
            [ngClass]="currentStep() >= step.number ? 'tw-bg-blue-600 tw-text-white' : 'tw-bg-gray-300 tw-text-gray-800'"
          >
            {{ step.number }}
          </div>
          <span class="tw-hidden sm:tw-inline tw-text-sm tw-font-medium tw-text-gray-800">{{ step.name }}</span>
        </div>

        @if (!last) {
          <div class="tw-mx-3 tw-h-0.5 tw-flex-1 tw-bg-gray-300"></div>
        }
      }
    </div>

    <!-- Step 1: Upload file -->
    @if (currentStep() === 1) {
      <div class="tw-flex tw-flex-col tw-gap-6">
        <div>
          <h5 class="tw-m-0 tw-mb-2 tw-text-base tw-font-semibold tw-text-gray-900">Upload your data file</h5>
          <p class="tw-m-0 tw-text-sm tw-text-gray-500">Upload a file containing your invitee data to begin the import process.</p>
        </div>

        <div
          class="tw-rounded-lg tw-border-2 tw-border-dashed tw-p-8 tw-text-center tw-cursor-pointer tw-transition-colors"
          [ngClass]="{
            'tw-border-gray-300 hover:tw-bg-gray-50': !isDragging() && !file() && !hasInvalidFileType(),
            'tw-border-blue-600 tw-bg-blue-50': isDragging(),
            'tw-border-gray-300 tw-bg-gray-50': !!file() && !hasInvalidFileType(),
            'tw-border-red-500 tw-bg-red-50': hasInvalidFileType()
          }"
          (click)="fileInput.click()"
          (dragover)="onDragOver($event)"
          (dragleave)="onDragLeave($event)"
          (drop)="onDrop($event)"
        >
          <input
            #fileInput
            type="file"
            class="tw-hidden"
            [accept]="supportedFormats"
            (change)="onFileChange($any($event.target).files)"
            (click)="$event.stopPropagation()"
          />

          @if (!file()) {
            <div class="tw-flex tw-flex-col tw-items-center tw-gap-2">
              <svg class="tw-h-12 tw-w-12 tw-text-blue-600" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                <path
                  d="M12 16V4M12 4L8 8M12 4L16 8"
                  stroke="currentColor"
                  stroke-width="1.5"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                />
                <path
                  d="M4 14V17C4 18.1046 4.89543 19 6 19H18C19.1046 19 20 18.1046 20 17V14"
                  stroke="currentColor"
                  stroke-width="1.5"
                  stroke-linecap="round"
                />
              </svg>
              <span class="tw-text-base tw-text-gray-800"><strong>Choose a file</strong> or drag it here</span>
              <small class="tw-text-sm tw-text-gray-500">Only .csv files are supported</small>
              @if (hasInvalidFileType()) {
                <p class="tw-m-0 tw-text-sm tw-text-red-600">Only .csv files are supported. Please try again.</p>
              }
            </div>
          } @else {
            <div class="tw-flex tw-items-center tw-justify-between tw-gap-4">
              <div class="tw-flex tw-items-center tw-gap-4 tw-min-w-0">
                <svg class="tw-h-8 tw-w-8 tw-shrink-0 tw-text-blue-600" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                  <path
                    d="M9 12H15M9 16H15M9 8H15M5 20H19C20.1046 20 21 19.1046 21 18V6C21 4.89543 20.1046 4 19 4H9.41421C9.149 4 8.89464 4.10536 8.70711 4.29289L4.29289 8.70711C4.10536 8.89464 4 9.149 4 9.41421V18C4 19.1046 4.89543 20 5 20Z"
                    stroke="currentColor"
                    stroke-width="1.5"
                    stroke-linecap="round"
                  />
                </svg>
                <div class="tw-text-left tw-min-w-0">
                  <div class="tw-font-medium tw-text-gray-900 tw-truncate">{{ file()?.name }}</div>
                  <div class="tw-text-sm tw-text-gray-500">{{ fileSizeLabel() }}</div>
                </div>
              </div>
              <button
                type="button"
                class="tw-flex tw-h-8 tw-w-8 tw-shrink-0 tw-items-center tw-justify-center tw-rounded-md tw-border-0 tw-bg-transparent tw-text-red-600 hover:tw-bg-red-50"
                (click)="removeFile($event)"
                aria-label="Remove file"
              >
                <svg class="tw-h-4 tw-w-4" viewBox="0 0 12 12" fill="none" aria-hidden="true">
                  <path
                    d="M7.20988 6L11.7388 1.47109C11.8205 1.39216 11.8857 1.29775 11.9305 1.19336C11.9754 1.08896 11.999 0.976687 12 0.863076C12.001 0.749465 11.9793 0.636794 11.9363 0.531639C11.8933 0.426484 11.8297 0.33095 11.7494 0.250611C11.669 0.170273 11.5735 0.106739 11.4684 0.0637164C11.3632 0.020694 11.2505 -0.000954946 11.1369 3.23065e-05C11.0233 0.00101956 10.911 0.0246235 10.8066 0.0694668C10.7023 0.11431 10.6078 0.179495 10.5289 0.261217L6 4.79012L1.47109 0.261217C1.30972 0.105355 1.09358 0.0191112 0.869234 0.0210607C0.644888 0.0230102 0.430283 0.112997 0.27164 0.27164C0.112997 0.430283 0.0230102 0.644888 0.0210607 0.869234C0.0191112 1.09358 0.105355 1.30972 0.261217 1.47109L4.79012 6L0.261217 10.5289C0.179495 10.6078 0.11431 10.7023 0.0694668 10.8066C0.0246235 10.911 0.00101956 11.0233 3.23065e-05 11.1369C-0.000954946 11.2505 0.020694 11.3632 0.0637164 11.4684C0.106739 11.5735 0.170273 11.669 0.250611 11.7494C0.33095 11.8297 0.426484 11.8933 0.531639 11.9363C0.636794 11.9793 0.749465 12.001 0.863076 12C0.976687 11.999 1.08896 11.9754 1.19336 11.9305C1.29775 11.8857 1.39216 11.8205 1.47109 11.7388L6 7.20988L10.5289 11.7388C10.6903 11.8946 10.9064 11.9809 11.1308 11.9789C11.3551 11.977 11.5697 11.887 11.7284 11.7284C11.887 11.5697 11.977 11.3551 11.9789 11.1308C11.9809 10.9064 11.8946 10.6903 11.7388 10.5289L7.20988 6Z"
                    fill="currentColor"
                  />
                </svg>
              </button>
            </div>
          }
        </div>

        <div class="tw-flex tw-items-center tw-justify-center tw-gap-2">
          <p class="tw-m-0 tw-text-sm tw-text-gray-500 tw-flex tw-items-center tw-gap-2.5">
            Don't have a template?
            <button
              type="button"
              class="tw-inline-flex tw-items-center tw-gap-1 tw-rounded-md tw-border tw-border-blue-600 tw-bg-white tw-px-3 tw-py-1.5 tw-text-sm tw-font-medium tw-text-blue-600 hover:tw-bg-blue-50"
              (click)="downloadTemplateClick()"
            >
              <svg class="tw-h-4 tw-w-4 tw-text-blue-600" viewBox="0 0 16 16" fill="none" aria-hidden="true">
                <path
                  d="M8 1.33333V10M8 10L5.33333 7.33333M8 10L10.6667 7.33333M2.66667 10V12.6667C2.66667 13.403 3.26362 14 4 14H12C12.7364 14 13.3333 13.403 13.3333 12.6667V10"
                  stroke="currentColor"
                  stroke-width="1.5"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                />
              </svg>
              Download Template
            </button>
          </p>
        </div>

        @if (file() && !isParsingFile() && fileRows().length === 0 && !hasInvalidFileType()) {
          <div class="tw-flex tw-items-center tw-gap-2">
            <ng-container [ngTemplateOutlet]="warningIcon" />
            <p class="tw-m-0 tw-text-sm tw-text-red-600">Please upload a file with at least 1 record to proceed.</p>
          </div>
        }

        @if (isDuplicateHeaders()) {
          <div class="tw-flex tw-items-center tw-gap-2">
            <ng-container [ngTemplateOutlet]="warningIcon" />
            <p class="tw-m-0 tw-text-sm tw-text-red-600">Duplicate header found!</p>
          </div>
        }

        @if (fileRows().length > 0 && !isDuplicateHeaders()) {
          <div class="tw-flex tw-flex-col tw-gap-3">
            <div>
              <h5 class="tw-m-0 tw-mb-1 tw-text-base tw-font-semibold tw-text-gray-900">Data Preview</h5>
              <p class="tw-m-0 tw-text-sm tw-text-gray-500">First few rows from your file:</p>
            </div>

            <div class="tw-overflow-x-auto tw-rounded-lg tw-border tw-border-gray-200 tw-max-h-[300px]">
              <table class="tw-w-full tw-border-collapse tw-text-sm">
                <thead>
                  <tr class="tw-bg-gray-50">
                    @for (column of fileColumns(); track column) {
                      <th
                        class="tw-border-b tw-border-gray-200 tw-px-4 tw-py-3 tw-text-left tw-font-semibold tw-text-gray-800 tw-whitespace-nowrap tw-max-w-[100px] tw-truncate"
                        [title]="column"
                      >
                        {{ column }}
                      </th>
                    }
                  </tr>
                </thead>
                <tbody>
                  @for (row of fileRows(); track $index) {
                    <tr>
                      @for (column of fileColumns(); track column) {
                        <td
                          class="tw-border-b tw-border-gray-200 tw-px-4 tw-py-3 tw-text-gray-700 tw-whitespace-nowrap tw-max-w-[100px] tw-truncate"
                          [title]="row[column]"
                        >
                          {{ row[column] }}
                        </td>
                      }
                    </tr>
                  }
                </tbody>
              </table>
            </div>
          </div>
        }
      </div>
    }

    <!-- Step 2: Map columns -->
    @if (currentStep() === 2) {
      <div class="tw-flex tw-flex-col tw-gap-6">
        <div>
          <h5 class="tw-m-0 tw-mb-2 tw-text-base tw-font-semibold tw-text-gray-900">Map Your Columns</h5>
          <p class="tw-m-0 tw-text-sm tw-text-gray-500">Match system fields with columns from your file.</p>
        </div>

        <div class="tw-rounded-lg tw-bg-gray-50 tw-p-6">
          <div class="tw-mb-4 tw-grid tw-grid-cols-[1fr_auto_1fr] tw-gap-4 tw-text-sm tw-font-semibold tw-text-gray-800">
            <span>System Field</span>
            <span class="tw-w-5"></span>
            <span>Your Column</span>
          </div>

          <form [formGroup]="mappingForm" class="tw-flex tw-max-h-[240px] tw-flex-col tw-gap-4 tw-overflow-y-auto">
            @for (targetColumn of effectiveTargetColumns(); track targetColumn.field) {
              <div class="tw-grid tw-grid-cols-[1fr_auto_1fr] tw-items-start tw-gap-4">
                <div class="tw-flex tw-min-h-[42px] tw-items-center">
                  <span class="tw-text-sm tw-font-medium tw-text-gray-800">{{ targetColumn.header }}</span>
                </div>

                <div class="tw-flex tw-h-[42px] tw-w-5 tw-items-center tw-justify-center tw-text-gray-400">
                  <svg class="tw-h-4 tw-w-4" viewBox="0 0 16 16" fill="none" aria-hidden="true">
                    <path d="M3 8H13M13 8L9 4M13 8L9 12" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round" />
                  </svg>
                </div>

                <div class="tw-flex tw-flex-col tw-gap-1">
                  <select
                    class="mapping-select tw-h-[42px] tw-w-full tw-rounded-lg tw-border tw-border-gray-300 tw-bg-white tw-pl-3 tw-text-sm tw-text-gray-800 focus:tw-border-blue-600 focus:tw-outline-none focus:tw-ring-2 focus:tw-ring-blue-100"
                    [formControl]="getMappingControl(targetColumn.field)"
                    (change)="updateMapping($any($event.target).value, targetColumn.field)"
                  >
                    <option value="">{{ skipFieldOption }}</option>
                    @for (source of fileColumns(); track source) {
                      <option [value]="source">{{ source }}</option>
                    }
                  </select>
                </div>
              </div>
            }
          </form>

          <div class="tw-mt-4 tw-flex tw-items-start tw-gap-2 tw-rounded-lg tw-border tw-border-blue-100 tw-bg-blue-50 tw-p-3">
            <svg class="tw-mt-0.5 tw-h-4 tw-w-4 tw-shrink-0 tw-text-blue-600" viewBox="0 0 16 16" fill="none" aria-hidden="true">
              <path
                d="M8 1.33333C4.3181 1.33333 1.33333 4.3181 1.33333 8C1.33333 11.6819 4.3181 14.6667 8 14.6667C11.6819 14.6667 14.6667 11.6819 14.6667 8C14.6667 4.3181 11.6819 1.33333 8 1.33333ZM8.66667 11.3333H7.33333V7.33333H8.66667V11.3333ZM8 6.00001C7.63181 6.00001 7.33333 5.70152 7.33333 5.33334C7.33333 4.96515 7.63181 4.66667 8 4.66667C8.36819 4.66667 8.66667 4.96515 8.66667 5.33334C8.66667 5.70152 8.36819 6.00001 8 6.00001Z"
                fill="currentColor"
              />
            </svg>
            <p class="tw-m-0 tw-text-sm tw-text-gray-600">
              <strong>Tip:</strong> Map at least email or phone number. Other fields can be skipped and filled later.
            </p>
          </div>

          @if (!hasRequiredContactFieldMapped()) {
            <p class="tw-m-0 tw-mt-4 tw-text-center tw-text-sm tw-text-red-600">
              <strong>Error:</strong> please map at least one contact field (email or phone number).
            </p>
          }
        </div>
      </div>
    }

    <!-- Step 3: Review & import -->
    @if (currentStep() === 3) {
      <div class="tw-flex tw-flex-col tw-gap-6">
        <div>
          <h5 class="tw-m-0 tw-mb-2 tw-text-base tw-font-semibold tw-text-gray-900">Review & Import</h5>
          <p class="tw-m-0 tw-text-sm tw-text-gray-500">Review your import details before finalizing.</p>
        </div>

        <div class="tw-grid tw-grid-cols-1 tw-gap-4 sm:tw-grid-cols-2">
          <div class="tw-flex tw-items-center tw-gap-4 tw-rounded-lg tw-border tw-border-gray-200 tw-p-4">
            <svg class="tw-h-8 tw-w-8 tw-shrink-0 tw-text-blue-600" viewBox="0 0 24 24" fill="none" aria-hidden="true">
              <path
                d="M17 20C17 18.9391 16.5786 17.9217 15.8284 17.1716C15.0783 16.4214 14.0609 16 13 16H5C3.93913 16 2.92172 16.4214 2.17157 17.1716C1.42143 17.9217 1 18.9391 1 20M23 20C23 18.9391 22.5786 17.9217 21.8284 17.1716C21.0783 16.4214 20.0609 16 19 16H18.5M16 7C16 9.20914 14.2091 11 12 11C9.79086 11 8 9.20914 8 7C8 4.79086 9.79086 3 12 3C14.2091 3 16 4.79086 16 7ZM19 8V14M19 11H13"
                stroke="currentColor"
                stroke-width="1.5"
                stroke-linecap="round"
                stroke-linejoin="round"
              />
            </svg>
            <div>
              <div class="tw-text-sm tw-text-gray-500">Records to Import</div>
              <div class="tw-text-2xl tw-font-semibold tw-text-gray-900">{{ recordsToImport() }}</div>
            </div>
          </div>

          <div class="tw-flex tw-items-center tw-gap-4 tw-rounded-lg tw-border tw-border-gray-200 tw-p-4">
            <svg class="tw-h-8 tw-w-8 tw-shrink-0 tw-text-blue-600" viewBox="0 0 24 24" fill="none" aria-hidden="true">
              <path
                d="M4 6C4 4.89543 4.89543 4 6 4H18C19.1046 4 20 4.89543 20 6V18C20 19.1046 19.1046 20 18 20H6C4.89543 20 4 19.1046 4 18V6Z"
                stroke="currentColor"
                stroke-width="1.5"
              />
              <path d="M4 10H20M10 4V20" stroke="currentColor" stroke-width="1.5" />
            </svg>
            <div>
              <div class="tw-text-sm tw-text-gray-500">Mapped Fields</div>
              <div class="tw-text-2xl tw-font-semibold tw-text-gray-900">{{ mappedColumnsCount() }}</div>
            </div>
          </div>
        </div>

        <div class="tw-flex tw-flex-col tw-gap-3">
          <h6 class="tw-m-0 tw-text-sm tw-font-semibold tw-text-gray-900">Column Mapping Summary</h6>

          <div class="tw-max-h-[150px] tw-overflow-y-auto tw-rounded-lg tw-border tw-border-gray-200 tw-p-4">
            @if (hasNoMappedColumns()) {
              <p class="tw-m-0 tw-text-sm tw-italic tw-text-red-500">No columns have been mapped. Please go back and map at least one column.</p>
            } @else {
              <div class="tw-mb-3 tw-grid tw-grid-cols-[1fr_auto_1fr] tw-gap-4 tw-text-sm tw-font-semibold tw-text-gray-800">
                <span>System Field</span>
                <span class="tw-w-5"></span>
                <span>Your Column</span>
              </div>

              @for (mapping of columnMappingRows(); track mapping.targetColumn) {
                <div class="tw-mb-2 tw-grid tw-grid-cols-[1fr_auto_1fr] tw-items-center tw-gap-4 tw-text-sm">
                  <span class="tw-text-gray-800">{{ mapping.targetHeader }}</span>
                  <span class="tw-flex tw-w-5 tw-justify-center tw-text-gray-400">
                    <svg class="tw-h-4 tw-w-4" viewBox="0 0 16 16" fill="none" aria-hidden="true">
                      <path d="M3 8H13M13 8L9 4M13 8L9 12" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round" />
                    </svg>
                  </span>
                  <span class="tw-text-gray-600">{{ mapping.sourceColumn || '-- Skipped --' }}</span>
                </div>
              }
            }
          </div>
        </div>
      </div>
    }

    <ng-template #warningIcon>
      <svg class="tw-h-5 tw-w-5 tw-shrink-0 tw-text-amber-500" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
        <path
          fill-rule="evenodd"
          d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z"
          clip-rule="evenodd"
        />
      </svg>
    </ng-template>
  </div>

  <div slot="footer" class="tw-flex tw-items-center tw-justify-between tw-gap-4 tw-px-6 tw-py-4 tw-border-t tw-border-gray-200">
    <div>
      @if (currentStep() > 1) {
        <button
          type="button"
          class="tw-rounded-md tw-border tw-border-gray-300 tw-bg-white tw-px-5 tw-py-2.5 tw-text-sm tw-font-medium tw-text-gray-800 hover:tw-bg-gray-50 disabled:tw-cursor-not-allowed disabled:tw-opacity-50"
          [disabled]="isImporting()"
          (click)="goToPreviousStep()"
        >
          Back
        </button>
      } @else {
        <button
          type="button"
          class="tw-rounded-md tw-border tw-border-gray-300 tw-bg-white tw-px-5 tw-py-2.5 tw-text-sm tw-font-medium tw-text-gray-800 hover:tw-bg-gray-50"
          (click)="close()"
        >
          Cancel
        </button>
      }
    </div>

    <div>
      @if (currentStep() < wizardSteps.length) {
        <button
          type="button"
          class="tw-inline-flex tw-items-center tw-gap-2 tw-rounded-md tw-border-0 tw-bg-blue-600 tw-px-5 tw-py-2.5 tw-text-sm tw-font-medium tw-text-white hover:tw-bg-blue-700 disabled:tw-cursor-not-allowed disabled:tw-opacity-50"
          [disabled]="isNextDisabled()"
          (click)="goToNextStep()"
        >
          Next
          <svg class="tw-h-4 tw-w-4 tw-text-white" viewBox="0 0 16 16" fill="none">
            <path d="M6 4L10 8L6 12" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round" />
          </svg>
        </button>
      } @else {
        <button
          type="button"
          class="tw-inline-flex tw-items-center tw-gap-2 tw-rounded-md tw-border-0 tw-bg-blue-600 tw-px-5 tw-py-2.5 tw-text-sm tw-font-medium tw-text-white hover:tw-bg-blue-700 disabled:tw-cursor-not-allowed disabled:tw-opacity-50"
          [disabled]="!canImportProceed() || isImporting()"
          (click)="importData()"
        >
          @if (isImporting()) {
            Importing...
          } @else {
            <svg class="tw-h-4 tw-w-4 tw-text-white" viewBox="0 0 16 16" fill="none">
              <path
                d="M8 1.33333V10M8 10L5.33333 7.33333M8 10L10.6667 7.33333M2.66667 10V12.6667C2.66667 13.403 3.26362 14 4 14H12C12.7364 14 13.3333 13.403 13.3333 12.6667V10"
                stroke="currentColor"
                stroke-width="1.5"
                stroke-linecap="round"
                stroke-linejoin="round"
              />
            </svg>
            Import Data
          }
        </button>
      }
    </div>
  </div>
</modal>
